// composables/useCollectionContext.ts — agency members: where am I working, and switch (mig 1230, M-AG-001).
import { computed, type Ref, type ComputedRef } from 'vue'
import { useState } from '#imports'
import type { CollectionContext } from '~/types/collections'
import { writeContextPing, translateCollectionContextError } from '~/utils/collectionContext'

export interface UseCollectionContext {
  contexts:         Ref<CollectionContext[]>
  current:          ComputedRef<CollectionContext | null>
  home:             ComputedRef<CollectionContext | null>
  isAgencyMember:   ComputedRef<boolean>
  isInClientClinic: ComputedRef<boolean>
  loading:          Ref<boolean>
  loaded:           Ref<boolean>
  error:            Ref<string | null>
  load():                    Promise<void>
  switchTo(clinicId: string): Promise<void>
}

export function useCollectionContext(): UseCollectionContext {
  const supabase = useSupabaseClient()
  // Shared across the layout, the bar and the guard plugin (one RPC per page load).
  const contexts = useState<CollectionContext[]>('collection-contexts', () => [])
  const loading  = useState<boolean>('collection-contexts-loading', () => false)
  const loaded   = useState<boolean>('collection-contexts-loaded', () => false)
  const error    = useState<string | null>('collection-contexts-error', () => null)

  const current          = computed(() => contexts.value.find(c => c.is_current) ?? null)
  const home             = computed(() => contexts.value.find(c => c.is_home) ?? null)
  const isAgencyMember   = computed(() => contexts.value.length > 0)
  const isInClientClinic = computed(() => !!current.value && !current.value.is_home)

  async function load(): Promise<void> {
    loading.value = true
    error.value   = null
    try {
      const { data, error: e } = await supabase.rpc('get_my_collection_contexts')
      if (e) throw e
      contexts.value = (data ?? []) as CollectionContext[]
      loaded.value = true
    } catch (e: any) {
      // not_agency_member can also fire here (membership revoked mid-session) — map it
      // the same way as switchTo so no raw RAISE code ever reaches error.value (it's a
      // useState singleton shared by the bar, the layout and the guard plugin).
      error.value = translateCollectionContextError(e)
      contexts.value = []
    } finally {
      loading.value = false
    }
  }

  /** RPC → localStorage ping (other tabs reload) → FULL reload: every Pinia store,
   *  realtime channel and cached clinic module belongs to the previous tenant. */
  async function switchTo(clinicId: string): Promise<void> {
    loading.value = true
    error.value   = null
    try {
      const { data, error: e } = await supabase.rpc('switch_collection_context', { p_clinic_id: clinicId })
      if (e) throw e
      try { writeContextPing(window.localStorage, (data as any)?.clinic_id ?? clinicId) } catch { /* private mode */ }
      window.location.assign('/cobranza')
      // loading stays true on purpose: the page is unloading.
    } catch (e: any) {
      error.value   = translateCollectionContextError(e)
      loading.value = false
      throw e
    }
  }

  return { contexts, current, home, isAgencyMember, isInClientClinic, loading, loaded, error, load, switchTo }
}
